import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const MAX_VISIBLE_TOASTS = 3;

const ToastContext = createContext({
  showToast: () => {},
  showErrorToast: () => {},
});

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const removeTimersRef = useRef(new Map());

  const clearToastTimer = useCallback((toastId) => {
    const timer = removeTimersRef.current.get(toastId);

    if (timer) {
      window.clearTimeout(timer);
      removeTimersRef.current.delete(toastId);
    }
  }, []);

  const showToast = useCallback((message, options = {}) => {
    if (!message) return;
    const { type = 'info', timeout = 1500 } = options;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => {
      const overflowToasts = prev.slice(MAX_VISIBLE_TOASTS - 1);

      overflowToasts.forEach((toast) => {
        clearToastTimer(toast.id);
      });

      return [{ id, message, type, leaving: false }, ...prev.slice(0, MAX_VISIBLE_TOASTS - 1)];
    });
    const removeTimer = window.setTimeout(() => {
      setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)));
      const finalizeTimer = window.setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
        removeTimersRef.current.delete(id);
      }, 440);
      removeTimersRef.current.set(id, finalizeTimer);
    }, timeout);
    removeTimersRef.current.set(id, removeTimer);
  }, [clearToastTimer]);

  const showErrorToast = useCallback((message, options = {}) => {
    showToast(message, { ...options, type: 'error' });
  }, [showToast]);

  const value = useMemo(() => ({
    showToast,
    showErrorToast,
  }), [showToast, showErrorToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-alert-stack">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast-alert toast-alert--global${toast.type === 'error' ? ' toast-alert--error' : ''}${toast.leaving ? ' toast-alert--leaving' : ''}`}
          >
            <p>{toast.message}</p>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
